import { FC } from "react"
import { Component } from "../../types/ContentStructure"
import { Box, Typography } from "@mui/material"
import ComponentImage from "./ComponentImage"

interface ComponentBannerTextEmphasisProps {
    component: Component
    sx?: any
}

/**
 * BANNER TEXTO ÉNFASIS
 * Variante de ComponentBannerText donde el subTitle se pinta con el color
 * de acento del catTheme asignado en CMS (theme.themeStyles.backgroundColor).
 *
 * Campos:
 *   title    → texto introductorio, fontWeight=400, color #545454
 *   subTitle → texto con énfasis, fontWeight=700, color = theme.backgroundColor
 *   text     → cuerpo de apoyo opcional, color #545454
 *   image    → logo/imagen opcional arriba del texto
 *
 * El color del énfasis se controla 100% desde CMS Admin asignando el catTheme adecuado.
 * El fondo siempre es #F5F5F5 (excepción aprobada igual que BANNER TEXTO).
 */
const ComponentBannerTextEmphasis: FC<ComponentBannerTextEmphasisProps> = ({ component, sx = {} }) => {
    const { theme, title, subTitle, text, image } = component

    // El color de énfasis viene del catTheme.backgroundColor asignado en CMS
    const emphasisColor = theme?.themeStyles?.backgroundColor || '#001933'

    return (
        <Box sx={[
            {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                py: { xs: 3, md: 5 },
                px: { xs: 3, md: 8 },
                width: '100%',
                backgroundColor: '#F5F5F5',
                color: '#545454',
                mb: 6,
            },
            { ...sx }
        ]}>
            {/* Logo / imagen opcional */}
            {image && (
                <ComponentImage
                    component={component}
                    sx={{
                        height: 'auto',
                        width: 'auto',
                        maxHeight: 60,
                        objectFit: 'contain',
                        backgroundColor: 'transparent',
                        mb: 2,
                    }}
                />
            )}

            <Typography variant="h2" sx={{
                fontWeight: 700,
                maxWidth: '60ch',
                mb: 0.5,
                color: '#545454',
            }}>
                {title}
                {subTitle && (
                    <>
                        {' '}
                        <b style={{ color: emphasisColor }}>
                            {subTitle}
                        </b>
                    </>
                )}
            </Typography>
            {/* Texto de apoyo opcional */}
            {text && (
                <Typography variant="body1" sx={{
                    mt: 1.5,
                    maxWidth: '60ch',
                    color: '#545454',
                }}>
                    {text}
                </Typography>
            )}
        </Box>
    )
}

export default ComponentBannerTextEmphasis
